import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { Orb } from '@sorare/core/src/components/Orb';
import { ANY_SPORT_SERIES_MISSIONS_DAILY } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useConfigContext } from '@sorare/core/src/contexts/config/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';

import { IconWrapper } from '../ui.tsx';
import type { DailyActionTaskWidget_dailyActionTask } from './__generated__/index.graphql.ts';
import dailyRunIcon from './assets/dailyRunIcon.svg';

type Props = { dailyActionTask: DailyActionTaskWidget_dailyActionTask };
export const DailyActionTaskWidget = ({ dailyActionTask }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  return (
    <Orb
      to={generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_DAILY)}
      color="var(--c-blue-400)"
      chipState={dailyTasksExpiresAt ? 'timeLeft' : undefined}
      label={
        dailyTasksExpiresAt ? (
          <TimeLeft time={dailyTasksExpiresAt} />
        ) : undefined
      }
    >
      <Badge
        badgeContent={
          (dailyActionTask.subtasks || []).filter(
            subtask =>
              subtask.aasmState === ManagerProgressionTaskState.COMPLETED
          ).length
        }
        overlap="circular"
      >
        <HalfCircularProgressBar
          size={5}
          progresses={(dailyActionTask.subtasks || [])
            .map(subtask =>
              subtask.aasmState === ManagerProgressionTaskState.CLAIMED ||
              subtask.aasmState === ManagerProgressionTaskState.COMPLETED
                ? 1
                : 0
            )
            .sort((a, b) => b - a)}
          colors={[
            { from: '#7029FF', to: '#7029FF' },
            { from: '#7029FF', to: '#7029FF' },
            { from: '#7029FF', to: '#7029FF' },
          ]}
          backgroundColor="#100526"
        />
        <IconWrapper>
          <img src={dailyRunIcon} alt="play" width={24} height={24} />
        </IconWrapper>
      </Badge>
    </Orb>
  );
};

DailyActionTaskWidget.fragments = {
  dailyActionTask: gql`
    fragment DailyActionTaskWidget_dailyActionTask on DailyActionTask {
      id
      subtasks {
        id
        aasmState
      }
    }
  ` as TypedDocumentNode<DailyActionTaskWidget_dailyActionTask>,
};
